import { Chip, Stack } from '@mui/joy';
import { Funnel, X } from '@phosphor-icons/react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'core/components';
import { ITssDashboardFilters } from 'reports/components';
import { Color, Direction, Size, Variant } from 'theme/enums';

import { FilterPanel } from './FilterPanel';

interface IProps {
  onFiltersChange: (filters: ITssDashboardFilters) => void;
}

export const Filter = ({ onFiltersChange }: IProps) => {
  const [filters, setFilters] = useState<ITssDashboardFilters>({});
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);

  const { t } = useTranslation();

  useEffect(() => {
    onFiltersChange(filters);
  }, [filters, onFiltersChange]);

  const activeFiltersCount = useMemo(
    () =>
      (filters.theatreName?.length ?? 0) +
      (filters.creatorName ? 1 : 0) +
      (filters.customerCompanyName ? 1 : 0),
    [filters]
  );

  const onRemoveTheatre = (theatre: string) => {
    setFilters((prev) => ({
      ...prev,
      theatreName: prev.theatreName?.filter((t) => t !== theatre) ?? undefined,
    }));
  };

  const onRemoveOwner = () => {
    setFilters((prev) => ({
      ...prev,
      creatorName: undefined,
    }));
  };

  const onRemoveClient = () => {
    setFilters((prev) => ({
      ...prev,
      customerCompanyName: undefined,
    }));
  };

  const onResetAll = () => {
    setFilters({});
  };

  return (
    <>
      <Stack
        direction={Direction.ROW}
        sx={{ alignItems: 'center', gap: 1, justifyContent: 'space-between' }}
      >
        <Stack direction={Direction.ROW} sx={{ alignItems: 'center', gap: 1 }}>
          {activeFiltersCount > 0 && (
            <Button
              text={t('buttonLabelResetAll')}
              variant={Variant.PLAIN}
              color={Color.DANGER}
              size={Size.SM}
              onClick={onResetAll}
            />
          )}

          {filters.theatreName?.map((theatre: string) => (
            <Chip
              key={theatre}
              color={Color.PRIMARY}
              variant={Variant.SOFT}
              size={Size.SM}
              endDecorator={<X size={16} />}
              onClick={() => onRemoveTheatre(theatre)}
            >
              {theatre}
            </Chip>
          ))}

          {filters.creatorName && (
            <Chip
              color={Color.PRIMARY}
              variant={Variant.SOFT}
              size={Size.SM}
              endDecorator={<X size={16} />}
              onClick={onRemoveOwner}
            >
              {t('labelOwnerWithValue', { value: filters.creatorName })}
            </Chip>
          )}

          {filters.customerCompanyName && (
            <Chip
              color={Color.PRIMARY}
              variant={Variant.SOFT}
              size={Size.SM}
              endDecorator={<X size={16} />}
              onClick={onRemoveClient}
            >
              {t('labelCustomerWithValue', { value: filters.customerCompanyName })}
            </Chip>
          )}
        </Stack>

        <Button
          text={t('buttonLabelAllFilters')}
          icon={<Funnel />}
          variant={Variant.OUTLINED}
          color={Color.NEUTRAL}
          onClick={() => setIsFilterPanelOpen(true)}
          badges={
            activeFiltersCount > 0
              ? [
                  {
                    color: Color.PRIMARY,
                    key: 'activeFilters',
                  },
                ]
              : undefined
          }
        />
      </Stack>

      <FilterPanel
        isOpen={isFilterPanelOpen}
        onClose={() => setIsFilterPanelOpen(false)}
        value={filters}
        onChange={setFilters}
      />
    </>
  );
};
